import { useEffect } from 'react';
import { useUiModalStore, setSharedRequestOpen, setActiveTab } from '../store/uiModalStore';
import shareService from '../services/shareService';
import { decryptSnapshotData } from '../utils/shareEnvelope';
import { mapApiRequestToSidebar } from '../utils/requestMappers';
import { isApiClientEnabled } from '../extensions/store';
import { notifyApiClientOff } from '../extensions/apiClientOffNotice';

/**
 * Play on a shared-request chat card: fetch the (recipient-scoped) share data
 * and open it as a NEW UNSAVED request tab in the builder — the user can send
 * it immediately and save it the normal way. Lives at App level because
 * that's where `createNewRequestTab` exists; the card just sets
 * `uiModalStore.sharedRequestOpen = { token, label, channelId, enc }`.
 */
export function useSharedRequestOpen({ createNewRequestTab, showToast }) {
  const pending = useUiModalStore((s) => s.sharedRequestOpen);

  useEffect(() => {
    if (!pending?.token) return undefined;
    // API Client switched off (Settings → Extensions): open nothing, say so,
    // and open the shared request once the user turns it back on.
    if (!isApiClientEnabled()) {
      setSharedRequestOpen(null);
      notifyApiClientOff({ reopen: () => setSharedRequestOpen(pending) });
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        const shareData = await shareService.fetchShareData(pending.token);
        if (cancelled) return;
        if (shareData?.type !== 'request' || !shareData.data) {
          throw new Error('This share is not a request');
        }
        // An encrypted snapshot arrives as an envelope: a plaintext skeleton
        // (method only, here) plus ciphertext. Opening it as-is would put an
        // empty request in the builder, so decrypt first — and let the
        // envelope's own error text through, because "needs a newer version"
        // and "can't be opened on this account" are different problems.
        const data = await decryptSnapshotData(pending.channelId ?? null, shareData.data);
        if (cancelled) return;
        const mapped = mapApiRequestToSidebar(data);
        // No id → createNewRequestTab marks the tab unsaved (isNewRequest).
        delete mapped.id;
        createNewRequestTab({
          ...mapped,
          name: mapped.name || pending.label || 'Shared Request',
        });
        setActiveTab('collections');
      } catch (e) {
        if (!cancelled) showToast?.(e.message || 'Failed to open shared request', 'error');
      } finally {
        if (!cancelled) setSharedRequestOpen(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pending, createNewRequestTab, showToast]);
}
